
import React from 'react';
import { Edit, Trash, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import CountryFlag from '@/components/country/CountryFlag';
import { CountryItem } from '../types';
import { CountryStatus } from './CountryStatus';
import { HeroImageStatus } from './HeroImageStatus';
import { useRegions } from '@/hooks/useRegions';

interface MobileCountryCardProps {
  country: CountryItem;
  editing: boolean;
  editData: CountryItem | null;
  onEditDataChange: (data: CountryItem) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onOpenDialog: (country: CountryItem) => void;
  onToggleActive: (id: string) => void;
}

export const MobileCountryCard: React.FC<MobileCountryCardProps> = ({
  country,
  editing,
  editData,
  onEditDataChange,
  onSave,
  onDelete,
  onOpenDialog,
  onToggleActive,
}) => {
  const { regions } = useRegions();

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <CountryFlag countryCode={country.code} className="h-6 w-8" />
            <div>
              {editing ? (
                <div className="space-y-2">
                  <Input
                    value={editData?.name || ''}
                    onChange={(e) => onEditDataChange({ ...editData!, name: e.target.value })}
                    className="font-medium"
                  />
                  <Input
                    value={editData?.code || ''}
                    onChange={(e) => onEditDataChange({ ...editData!, code: e.target.value.toUpperCase() })}
                    className="w-20"
                    maxLength={2}
                  />
                </div>
              ) : (
                <>
                  <h3 className="font-medium">{country.name}</h3>
                  <p className="text-sm text-muted-foreground">{country.code}</p>
                </>
              )}
            </div>
          </div>
          <CountryStatus 
            active={country.active} 
            onToggle={() => onToggleActive(country.id)} 
          />
        </div>

        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Region:</span>
            {editing ? (
              <Select 
                value={editData?.region_id || "none"} 
                onValueChange={(value) => onEditDataChange({ ...editData!, region_id: value === "none" ? undefined : value })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Region</SelectItem>
                  {regions
                    .filter(region => region.active)
                    .map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm">
                {country.region_name || 'No Region'}
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Hero Image:</span>
            <HeroImageStatus imageUrl={country.background_image_url} />
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          {editing ? (
            <Button size="sm" onClick={onSave}>
              <Save size={16} className="mr-1" />
              Save
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => onOpenDialog(country)}>
                <Edit size={16} className="mr-1" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(country.id)}>
                <Trash size={16} className="mr-1" />
                Delete
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
